import * as React from "react";
import type { RecipeStep } from "@recipevault/shared";
import { Button } from "../../ui/button.js";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ParallelHintCardProps {
  /** Steps that can run in parallel with the current step. */
  parallelSteps: RecipeStep[];
  /** Called with the step id when the user taps "Start now". */
  onStart: (stepId: string) => void;
}

// ---------------------------------------------------------------------------
// ParallelHintCard
// ---------------------------------------------------------------------------

/**
 * Warm-amber bonus-opportunity card (spec §6.4).
 *
 * When another step lists the current step in `parallelWith`, show a card
 * per parallel step: "While this is going, also: <step>" + a [Start now]
 * button (≥ 48 px touch target).
 *
 * Amber treatment (bg-amber-500/15 border-amber-500/40 text-amber-100) keeps
 * it visually distinct from the emerald primary-action buttons.
 *
 * Empty parallelSteps → renders null.
 */
export function ParallelHintCard({ parallelSteps, onStart }: ParallelHintCardProps) {
  if (parallelSteps.length === 0) return null;

  return (
    <div className="flex flex-col gap-3">
      {parallelSteps.map((step) => (
        <div
          key={step.id}
          className="flex items-center justify-between gap-4 rounded-lg border border-amber-500/40 bg-amber-500/15 px-4 py-3"
        >
          <div className="flex-1 min-w-0">
            <p className="mb-0.5 text-xs font-semibold uppercase tracking-wide text-amber-400">
              While this is going, also:
            </p>
            {/* Plain text — never innerHTML (spec convention) */}
            <p className="text-base text-amber-100 line-clamp-2">{step.text}</p>
          </div>
          <Button
            size="cook"
            className="shrink-0 bg-amber-500 text-zinc-900 hover:bg-amber-400 focus-visible:ring-amber-500/50"
            onClick={() => onStart(step.id)}
          >
            Start now
          </Button>
        </div>
      ))}
    </div>
  );
}
